import React from 'react';

type RuleCategory = 'all' | 'weapon' | 'armor';
type FilterCategory = 'all' | string;

interface EquipmentRulesTabProps {
  ruleCategory: RuleCategory;
  setRuleCategory: (cat: RuleCategory) => void;
  filterCategory: FilterCategory;
  setFilterCategory: (cat: FilterCategory) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredRules: Array<{
    name: string;
    description: string;
    category: string;
    type: 'weapon' | 'armor';
    triggers?: any[];
    effects?: any[];
    conditions?: any[];
  }>;
  allCategories: string[];
  getCategoryColor: (category: string) => string;
}

export const EquipmentRulesTab: React.FC<EquipmentRulesTabProps> = ({
  ruleCategory,
  setRuleCategory,
  filterCategory,
  setFilterCategory,
  searchTerm,
  setSearchTerm,
  filteredRules,
  allCategories,
  getCategoryColor
}) => {
  return (
    <div>
      {/* Filters */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search rules..."
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Rule Type */}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Rule Type</label>
            <select
              value={ruleCategory}
              onChange={(e) => setRuleCategory(e.target.value as RuleCategory)}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Rules</option>
              <option value="weapon">Weapon Rules</option>
              <option value="armor">Armor Rules</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {allCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Rules List */}
      <div className="space-y-3">
        {filteredRules.map((rule, index) => (
          <div
            key={`${rule.type}-${rule.name}-${index}`}
            className="bg-surface border border-line rounded-lg p-4"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-ink">{rule.name}</h3>
              <div className="flex gap-2">
                <span className={`px-2 py-1 text-xs rounded ${getCategoryColor(rule.category)}`}>
                  {rule.category}
                </span>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    rule.type === 'weapon'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                  }`}
                >
                  {rule.type}
                </span>
              </div>
            </div>
            <p className="text-ink mb-3">{rule.description}</p>

            {/* Mechanics Display */}
            <div className="border-t border-line pt-3 mt-2">
              {/* Triggers */}
              {rule.triggers && rule.triggers.length > 0 && (
                <div className="mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
                    Triggers:
                  </span>
                  <ul className="list-disc list-inside text-sm text-ink-dim ml-1">
                    {rule.triggers.map((trigger: any, i: number) => (
                      <li key={i}>
                        <span className="font-medium text-ink">
                          {trigger.type.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        {trigger.conditions && trigger.conditions.length > 0 && (
                          <span className="text-ink-faint italic">
                            {' '}
                            (when{' '}
                            {trigger.conditions
                              .map((c: any) => `${c.field} ${c.operator} ${c.value}`)
                              .join(', ')}
                            )
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Effects */}
              {rule.effects && rule.effects.length > 0 && (
                <div className="mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
                    Effects:
                  </span>
                  <ul className="list-disc list-inside text-sm text-ink-dim ml-1">
                    {rule.effects.map((effect: any, i: number) => (
                      <li key={i}>
                        {effect.description || (
                          <span>
                            <span className="font-medium">{effect.type}</span>: {effect.target}{' '}
                            {effect.value ? `(${JSON.stringify(effect.value)})` : ''}{' '}
                            {effect.formula ? `[${effect.formula}]` : ''}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Conditions */}
              {rule.conditions && rule.conditions.length > 0 && (
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
                    Conditions:
                  </span>
                  <ul className="list-disc list-inside text-sm text-ink-dim ml-1">
                    {rule.conditions.map((condition: any, i: number) => (
                      <li key={i}>
                        {condition.description ||
                          `${condition.field} ${condition.operator} ${condition.value}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredRules.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-faint text-lg">No rules found matching your criteria</p>
        </div>
      )}
    </div>
  );
};
